/**
 * ISOLATED DASHBOARD COMPONENT
 * Complete feature isolation with independent data loaders
 * No conflicts between features - each feature has its own state and loaders
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Dashboard.css";
import TypeAheadPicker from "./components/TypeAheadPicker";
import DataTable from "./components/DataTable";
import Chart from "./components/Chart";
import OptimizedChart from "./components/OptimizedChart";
import ExecutiveReport from "./components/ExecutiveReport";
import GuideMe from "./components/GuideMe";
import MetroDataTable from "./components/tables/MetroDataTable";
import { useIsolatedDataManagement } from "./hooks/useIsolatedDataManagement";
import { useIsolatedDataLoading } from "./hooks/useIsolatedDataLoading";
import { useAIChat } from "./hooks/useAIChat";
import { 
  crumbChainLabel,
  smartCrumbChainLabel
} from "./utils/helpers";

export default function DashboardIsolated() {
  const dataState = useIsolatedDataManagement();
  const aiChat = useAIChat();
  const renderRobotHint = (extraStyle = {}) => (
    <div
      className="controls-hint"
      style={{
        marginTop: 22,
        marginBottom: -13,
        color: "#1f2933",
        fontSize: "16.5px",
        fontWeight: 600,
        display: "flex",
        alignItems: "center",
        gap: 6,
        ...extraStyle,
      }}
    >
      <span style={{ fontSize: "22px", lineHeight: 1 }}>🤖</span>
      <span>Slide the cursor over the robot emoji to see explanations!</span>
    </div>
  );
  const [isLoading, setIsLoading] = useState(false);
  const [showLevelWarning, setShowLevelWarning] = useState(false);
  const [showBestPerformingWarning, setShowBestPerformingWarning] = useState(false);
  const [showNewMarketsTPWarning, setShowNewMarketsTPWarning] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showAskBox, setShowAskBox] = useState(false);
  const [showGuideMe, setShowGuideMe] = useState(false);
  const [guideSection, setGuideSection] = useState('general');
  const [showGuideDropdown, setShowGuideDropdown] = useState(false);
  const chatMessagesRef = useRef(null);
  const guideDropdownRef = useRef(null);
  
  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [aiChat.messages, aiChat.loading]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (guideDropdownRef.current && !guideDropdownRef.current.contains(event.target)) {
        setShowGuideDropdown(false);
      }
    }
    if (showGuideDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showGuideDropdown]);
  
  const {
    // Feature 1 State
    feature1Data,
    setFeature1Data,
    feature1Loading,
    feature1Error,

    // Feature 2 State
    feature2Data,
    setFeature2Data,
    feature2Loading,
    feature2Error,

    // Feature 4 State
    feature4Data,
    setFeature4Data,
    feature4Loading,
    feature4Error,

    // Shared UI State
    areaType,
    setAreaType,
    selectedName,
    setSelectedName,
    selectedMetro,
    setSelectedMetro,
    searchText,
    setSearchText,
    view,
    setView,
    minPopOnly,
    setMinPopOnly,
    showReport: stateShowReport,
    setShowReport: setStateShowReport,
    currentChartMetric,
    setCurrentChartMetric,
    linksByName,
    setLinksByName,
    linksByRegionId,
    setLinksByRegionId,

    // Shared Index State
    levelItems,
    crumbsByName,
    populationByCity,
    rawCityNames,

    // Feature Management
    switchToFeature,
    getCurrentFeatureData
  } = dataState;

  // Clear "select level" warning once an area level is chosen
  useEffect(() => {
    if (areaType) {
      setShowLevelWarning(false);
      setShowBestPerformingWarning(false);
      setShowNewMarketsTPWarning(false);
    }
  }, [areaType]);

  // Use isolated data loading
  const dataLoading = useIsolatedDataLoading({
    ...dataState,
    setIsLoading,
    setCurrentChartMetric
  });

  // Get current feature data - memoize to prevent unnecessary recalculations
  const currentFeature = useMemo(() => getCurrentFeatureData(), [
    view, 
    feature1Data, 
    feature1Loading, 
    feature1Error, 
    feature2Data, 
    feature2Loading, 
    feature2Error, 
    feature4Data, 
    feature4Loading, 
    feature4Error
  ]);

  // Current metro data - use useRef to maintain stable reference
  const currentMetroRef = useRef(null);
  const [countySnapshot, setCountySnapshot] = useState(null);
  
  const currentMetro = useMemo(() => {
    const targetMetro = selectedMetro || selectedName;
    console.log(`[CURRENT_METRO] Calculating currentMetro for:`, { 
      targetMetro, 
      selectedMetro, 
      selectedName, 
      areaType, 
      view,
      currentFeature: {
        data: currentFeature.data,
        loading: currentFeature.loading,
        error: currentFeature.error
      }
    });
    if (!targetMetro) {
      currentMetroRef.current = null;
      return null;
    }
    
    // Handle different area types with appropriate key formatting
    let searchKey;
    console.log(`[CURRENT_METRO] Before switch - targetMetro:`, targetMetro, "areaType:", areaType);
    switch (areaType) {
      case "Zipcode":
        // Support both unpadded ("1103") and padded ("01103"). We'll try both below.
        searchKey = String(targetMetro);
        break;
      case "State":
        searchKey = targetMetro;
        break;
      case "Metro":
        searchKey = targetMetro;
        break;
      case "County":
        searchKey = targetMetro;
        break;
      case "City":
        searchKey = targetMetro;
        break;
      case "Neighborhood":
        searchKey = targetMetro;
        break;
      default:
        searchKey = targetMetro;
    }
    console.log(`[CURRENT_METRO] After switch - searchKey:`, searchKey);
    const searchKeys = areaType === "Zipcode"
      ? [String(searchKey), String(searchKey).padStart(5, "0")]
      : [searchKey];

    // County-specific override: if we have a directly loaded snapshot row
    // for the selected county, prefer it over any generic lookup logic.
    if (
      areaType === "County" &&
      (view === "" || view === "metro" || view === "metro-snapshot") &&
      countySnapshot &&
      String(countySnapshot.CountyName || countySnapshot.County || countySnapshot.AreaName || "").trim() === String(targetMetro).trim()
    ) {
      currentMetroRef.current = countySnapshot;
      return countySnapshot;
    }
    
    // Get data from current feature
    const featureData = currentFeature.data;
    const latestByMetroKeys = featureData?.latestByMetro ? Object.keys(featureData.latestByMetro) : [];
    const hasData = latestByMetroKeys.length > 0;
    
    console.log(`[CURRENT_METRO] Feature data:`, {
      hasFeatureData: !!featureData,
      hasLatestByMetro: !!featureData?.latestByMetro,
      latestByMetroKeys,
      hasData,
      searchKey,
      view,
      sampleData: featureData?.latestByMetro ? latestByMetroKeys.slice(0, 2).map(key => ({
        key,
        data: featureData.latestByMetro[key]
      })) : []
    });
    
    // Check if data is available (must have keys, not just an empty object)
    if (!featureData || !featureData.latestByMetro || !hasData) {
      // Clear ref when data is empty to prevent using stale cached data
      if (currentMetroRef.current) {
        console.log(`[CURRENT_METRO] No feature data available - clearing stale ref`);
        currentMetroRef.current = null;
      }
      console.log(`[CURRENT_METRO] No feature data available - returning null`);
      return null;
    }
    
    // If we already have a valid result and the data contains the same key, keep it
    // But only if the data is actually loaded (hasData is true)
    if (currentMetroRef.current && hasData) {
      const currentKey = currentMetroRef.current[areaType] || 
                        currentMetroRef.current.Metro || 
                        currentMetroRef.current.City || 
                        currentMetroRef.current.CountyName || 
                        currentMetroRef.current.County ||
                        currentMetroRef.current.State ||
                        currentMetroRef.current.Zipcode ||
                        currentMetroRef.current.Neighborhood;
      // Verify the key still exists in the data before using cached value
      if (currentKey === searchKey && featureData.latestByMetro[currentKey]) {
        return currentMetroRef.current;
      }
    }
    
    // Direct hit (try all candidate keys for Zipcode)
    console.log(`[CURRENT_METRO] Checking direct hit for searchKeys:`, searchKeys);
    for (const key of searchKeys) {
      if (featureData.latestByMetro && featureData.latestByMetro[key]) {
        currentMetroRef.current = featureData.latestByMetro[key];
        console.log(`[CURRENT_METRO] Direct hit found:`, featureData.latestByMetro[key]);
        return featureData.latestByMetro[key];
      }
    }

    // Case-insensitive exact match
    const target = String(searchKey).toLowerCase();
    let hit = null;
    for (const k of Object.keys(featureData.latestByMetro || {})) {
      if (String(k).toLowerCase() === target) { 
        hit = k; 
        break; 
      }
    }
    
    // Substring fallback for small naming differences
    if (!hit) {
      for (const k of Object.keys(featureData.latestByMetro || {})) {
        if (String(k).toLowerCase().includes(target)) { 
          hit = k; 
          break; 
        }
      }
    }
    
    let result = hit ? featureData.latestByMetro[hit] : null;

    // Final safety net for County level: use fuzzy snapshot lookup if
    // direct / case-insensitive / substring matches all fail but we
    // clearly have data loaded for this level.
    if (!result && areaType === "County" && featureData.latestByMetro) {
      try {
        const { getLatestSnapshot } = require("./utils/helpers");
        result = getLatestSnapshot(searchKey, featureData.latestByMetro);
      } catch {
        // helpers import might fail in some bundlers; ignore and keep result null
      }
    }

    currentMetroRef.current = result;
    return result;
  }, [selectedMetro, selectedName, areaType, view, currentFeature, countySnapshot]);

  // Dedicated loader for Feature 1 County snapshots (isolated from Feature 2)
  useEffect(() => {
    if (areaType !== "County" || !selectedMetro) {
      setCountySnapshot(null);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const url = `/data/Merged_Last_Month_Data/county_5y_50k_merged.csv?v=${Date.now()}`;
        console.log(`[COUNTY_SNAPSHOT] Loading Feature 1 snapshot for County: ${selectedMetro} from ${url}`);
        const res = await fetch(url, { cache: "no-store" });
        if (!res.ok) {
          console.error(`[COUNTY_SNAPSHOT] Failed to load county snapshot CSV:`, res.status);
          if (!cancelled) setCountySnapshot(null);
          return;
        }

        const text = await res.text();
        const Papa = (await import("papaparse")).default;

        Papa.parse(text, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: "greedy",
          transformHeader: (h) => String(h || "").trim(),
          complete: (result) => {
            if (cancelled) return;
            const rows = Array.isArray(result.data) ? result.data : [];
            const target = String(selectedMetro).trim().toLowerCase();
            const match = rows.find((r) =>
              String(r.CountyName || r.County || "").trim().toLowerCase() === target
            );

            if (!match) {
              console.warn(`[COUNTY_SNAPSHOT] No snapshot row found for County: ${selectedMetro}`);
              setCountySnapshot(null);
              return;
            }

            const processed = {
              ...match,
              // Normalize key fields expected by MetroDataTable / snapshot UI
              County: match.CountyName || match.County,
              CountyName: match.CountyName || match.County,
              Metro: match.Metro,
              State: match.State,
              StateName: match.StateName,
              // Core metrics
              Price: match.Price,
              Rent: match.Rent,
              Inventory: match.Inventory,
              Days: match.Days,
              // Delta / YoY fields
              PriceDelta: match.Price_delta,
              PriceYoY: match.Price_YoY_delta,
              PriceYoYDelta: match.Price_YoY_delta,
              RentDelta: match.Rent_delta,
              InventoryDelta: match.Inventory_delta,
              DaysDelta: match.Days_delta ?? match.DOM_delta,
              // Forecast aliases
              f1m: match.f1m,
              f3m: match.f3m,
              f12m: match.f12m,
              Fcst1m: match.f1m,
              Fcst3m: match.f3m,
              Fcst12m: match.f12m,
            };

            console.log(`[COUNTY_SNAPSHOT] Snapshot row loaded for ${selectedMetro}:`, processed);
            setCountySnapshot(processed);
          },
          error: (err) => {
            console.error(`[COUNTY_SNAPSHOT] CSV parse error:`, err);
            if (!cancelled) setCountySnapshot(null);
          },
        });
      } catch (err) {
        console.error(`[COUNTY_SNAPSHOT] Unexpected error loading snapshot:`, err);
        if (!cancelled) setCountySnapshot(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [areaType, selectedMetro]);

  // Clear currentMetroRef when areaType changes to prevent stale data from wrong level
  useEffect(() => {
    console.log(`[CURRENT_METRO_DEBUG] AreaType changed to ${areaType} - clearing currentMetroRef`);
    currentMetroRef.current = null;
  }, [areaType]);

  // Debug currentMetro calculation
  useEffect(() => {
    console.log(`[CURRENT_METRO_DEBUG] County level debugging:`, {
      areaType,
      selectedMetro,
      selectedName,
      targetMetro: selectedMetro || selectedName,
      featureData: currentFeature.data,
      latestByMetroKeys: currentFeature.data?.latestByMetro ? Object.keys(currentFeature.data.latestByMetro) : [],
      currentMetro: currentMetro
    });
  }, [areaType, selectedMetro, selectedName, currentFeature.data, currentMetro]);

  const resetView = () => {
    console.log("[DASHBOARD_ISOLATED] Reset button clicked - starting reset process");
    console.log("[DASHBOARD_ISOLATED] Before reset - view:", view, "areaType:", areaType, "selectedMetro:", selectedMetro, "selectedName:", selectedName);
    dataState.resetAllFeatures();
    aiChat.reset();
    setShowAskBox(false);
    console.log("[DASHBOARD_ISOLATED] After reset - view:", view, "areaType:", areaType, "selectedMetro:", selectedMetro, "selectedName:", selectedName);
    console.log("[DASHBOARD_ISOLATED] Reset button clicked - all state cleared, should show welcome screen");
  };

  // Keep breadcrumb name in sync with the active selection (non-Metro only)
  useEffect(() => {
    if (areaType !== "Metro") {
      setSelectedName(selectedMetro || "");
    }
  }, [areaType, selectedMetro, setSelectedName]);

  // Keep selectedMetro in sync with selectedName for non-Metro views (needed for chart functionality)
  useEffect(() => {
    if (areaType !== "Metro" && selectedName && !selectedMetro) {
      setSelectedMetro(selectedName);
    }
  }, [areaType, selectedName, selectedMetro, setSelectedMetro]);

  // Keep selectedName in sync with selectedMetro for non-Metro views (needed for data loading)
  useEffect(() => {
    if (areaType !== "Metro" && selectedMetro && !selectedName) {
      setSelectedName(selectedMetro);
    }
  }, [areaType, selectedMetro, selectedName, setSelectedName]);

  // Simple showChart function that uses isolated loading
  const showChart = async (metric, target, regionId = null) => {
    console.log(`[DASHBOARD_ISOLATED] showChart called with metric: ${metric}, target: ${target}, regionId: ${regionId}`);
    console.log(`[DASHBOARD_ISOLATED] Current view: ${view}, areaType: ${areaType}`);
    console.log(`[DASHBOARD_ISOLATED] dataLoading object:`, dataLoading);
    
    // If RegionID not provided, derive from dropdown source metadata (crumbs) or latestByMetro
    let derivedRegionId = regionId;
    if (!derivedRegionId) {
      const fromCrumbs = crumbsByName?.[target]?.RegionID;
      const fromLatest = currentFeature.data?.latestByMetro?.[target]?.RegionID || currentFeature.data?.latestByMetro?.[target]?.regionId || currentFeature.data?.latestByMetro?.[target]?.region_id;
      derivedRegionId = fromCrumbs || fromLatest || null;
    }

    if (dataLoading.loadChartData && areaType && target) {
      console.log(`[DASHBOARD_ISOLATED] Triggering isolated chart loading for ${areaType}: ${target}, metric: ${metric}, regionId: ${derivedRegionId || regionId || 'missing'}`);
      
      // Clear existing chart data before loading new metric to prevent stale data
      // Use the appropriate setter based on current view
      if (view === "" || view === "metro" || view === "metro-snapshot" || view === "direct") {
        setFeature1Data(prev => ({ ...prev, chartData: [] }));
      } else if (view === "performance" || view === "performance-snapshot") {
        setFeature2Data(prev => ({ ...prev, chartData: [] }));
      } else if (view === "newMarketsTP" || view === "newMarketsTP-snapshot") {
        setFeature4Data(prev => ({ ...prev, chartData: [] }));
      }
      
      await dataLoading.loadChartData(metric, target, derivedRegionId);
    } else {
      console.log(`[DASHBOARD_ISOLATED] Chart loading not triggered - missing requirements:`, {
        hasLoadChartData: !!dataLoading.loadChartData,
        hasAreaType: !!areaType,
        hasTarget: !!target
      });
    }
  };

  return (
    <div className="dashboard">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <h1>
          <strong>AlgoreitAI</strong>{" "}
          <span style={{ fontWeight: 400 }}>Market Analysis</span>
        </h1>
        <div style={{ fontSize: 12, color: '#444' }}>Raw Data Source: Zillow; Mls, US Census Statistics</div>
      </div>

      {/* CONTROLS */}
      <div className="controls">
        {/* Level selector */}
        <div className="level-group">
          <label htmlFor="level">Level</label>
          <select
            id="level"
            value={areaType}
            onChange={(e) => {
              const newAreaType = e.target.value;
              console.log(`[DASHBOARD_ISOLATED] Level dropdown changed from ${areaType} to ${newAreaType}`);
              
              // Clear selection state when switching levels to prevent stale selections
              // This ensures we don't try to show data for the wrong level
              if (areaType !== newAreaType) {
                console.log(`[DASHBOARD_ISOLATED] Clearing selection state for level change from ${areaType} to ${newAreaType}`);
                setSelectedMetro("");
                setSelectedName("");
                setSearchText("");
                setView(""); // Clear view to show empty state until new selection is made
              }
              
              // Change the area type
              setAreaType(newAreaType);
            }}
          >
            <option value="">
              Select area level
            </option>
            <option value="Metro">Metro</option>
            <option value="State">State</option>
            <option value="County">County</option>
            <option value="City">City</option>
            <option value="Zipcode">Zipcode</option>
            <option value="Neighborhood">Neighborhood</option>
          </select>
        </div>

        {/* Type-ahead with warning message */}
        <div style={{ position: 'relative' }}>
          <TypeAheadPicker
            areaType={areaType}
            allDates={currentFeature.data?.allDates || []}
            latestByMetro={currentFeature.data?.latestByMetro || {}}
            levelItems={levelItems}
            minPopOnly={minPopOnly}
            populationByCity={populationByCity}
            searchText={searchText}
            setSearchText={setSearchText}
            feature={!(view === "performance" || view === "newMarketsTP") ? "feature1" : undefined}
            crumbsByName={crumbsByName}
            onCommit={(payload) => {
              const name = typeof payload === "object" && payload !== null ? payload.name : payload;
              console.log(`[DASHBOARD_ISOLATED] onCommit called for: ${name}, areaType: ${areaType}`);
              setSelectedMetro(name);
              setSelectedName(name);
              // Always set view to "metro" to show the selected item's data
              setView("metro");
            }}
            onRequireAreaType={() => setShowLevelWarning(true)}
          />
          {showLevelWarning && !areaType && (
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: "24px",
                fontSize: "13px",
                color: "#fbc02d", // yellow text
                whiteSpace: 'nowrap'
              }}
            >
              Please select area level first
            </div>
          )}
        </div>

        {/* Action buttons - ISOLATED FEATURE SWITCHING */}
        <button 
          className="btn" 
          disabled={!areaType}
          onClick={() => {
            if (!areaType) {
              setShowBestPerformingWarning(true);
              return;
            }
            switchToFeature("performance");
          }}
          title={!areaType ? "Please select area level first" : ""}
        >
          Best Performing
        </button>
        {showBestPerformingWarning && !areaType && (
          <div
            style={{
              display: 'inline-block',
              marginLeft: "8px",
              fontSize: "13px",
              color: "#fbc02d", // yellow text
              whiteSpace: 'nowrap'
            }}
          >
            Please select area level first
          </div>
        )}
        <button 
          className="btn" 
          disabled={!areaType}
          onClick={() => {
            if (!areaType) {
              setShowNewMarketsTPWarning(true);
              return;
            }
            switchToFeature("newMarketsTP");
          }}
          title={!areaType ? "Please select area level first" : ""}
        >
          New Markets TP
        </button>
        {showNewMarketsTPWarning && !areaType && (
          <div
            style={{
              display: 'inline-block',
              marginLeft: "8px",
              fontSize: "13px",
              color: "#fbc02d", // yellow text
              whiteSpace: 'nowrap'
            }}
          >
            Please select area level first
          </div>
        )}
        <button 
          className="btn" 
          onClick={async () => {
            console.log('[EXECUTIVE_REPORT] Button clicked');

            // Preload Price chart data for the selected area only if it has
            // not already been loaded. This gives the Executive Report a full
            // time series without making redundant network requests or using
            // any extra fallbacks inside the report itself.
            const target = selectedMetro || selectedName;
            const hasChartData = Array.isArray(currentFeature.data?.chartData) && currentFeature.data.chartData.length > 0;

            if (target && !hasChartData) {
              try {
                console.log('[EXECUTIVE_REPORT] Preloading chart data via showChart for:', {
                  target,
                  areaType,
                  view,
                  hasChartData
                });
                // Use the same helper that the main Charts UI uses so we
                // get a proper RegionID-based time series.
                await showChart('Price', target);
              } catch (err) {
                console.error('[EXECUTIVE_REPORT] Error preloading chart data:', err);
              }
            }

            setShowReport(true);
          }}
          disabled={!currentMetro}
          title={!currentMetro ? "Please select an area to view the Executives Report" : ""}
        >
          Executives Report
        </button>
        {/* FEATURE 6 DISABLED - Ask Me a Question button removed */}
        {/* <button className="btn" onClick={() => setShowAskBox(v => !v)}>
          Ask Me a Question
        </button> */}

        {/* Guide Me Button with Dropdown */}
        <div style={{ position: 'relative', display: 'inline-block' }} ref={guideDropdownRef}>
          <button 
            className="btn" 
            onClick={() => setShowGuideDropdown(!showGuideDropdown)}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '6px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              fontWeight: '600',
              boxShadow: '0 2px 8px rgba(102, 126, 234, 0.4)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.4)';
            }}
          >
            Guide Me
            <span style={{ fontSize: '12px' }}>▼</span>
          </button>
          {showGuideDropdown && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: '4px',
              backgroundColor: 'white',
              border: '1px solid #ddd',
              borderRadius: '4px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 1000,
              minWidth: '200px',
              overflow: 'hidden'
            }}>
              <button
                onClick={() => {
                  setGuideSection('general');
                  setShowGuideMe(true);
                  setShowGuideDropdown(false);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: 'white',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '14px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
              >
                💡 Insights
              </button>
              <div style={{ height: '1px', backgroundColor: '#e8e8e8' }}></div>
              <button
                onClick={() => {
                  setGuideSection('features');
                  setShowGuideMe(true);
                  setShowGuideDropdown(false);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: 'white',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '14px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
              >
                📚 Features Guide
              </button>
            </div>
          )}
        </div>

        <button 
          className="btn btn-reset" 
          onClick={() => {
            console.log("RESET BUTTON CLICKED - TEST");
            resetView();
          }}
          style={{
            background: '#6c757d',
            color: 'white',
            border: '1px solid #5a6268'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#5a6268';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#6c757d';
          }}
        >
          Reset
        </button>
      </div>

      {/* Info message for Zipcode and Neighborhood - always visible */}
      {(areaType === 'Zipcode' || areaType === 'Neighborhood') && (
        <div style={{
          margin: "6px 0 10px",
          padding: "8px 12px",
          background: "#fff3cd",
          border: "1px solid #ffeaa7",
          borderRadius: "6px",
          fontSize: "13px",
          color: "#856404"
        }}>
          💡 {areaType === 'Zipcode' 
            ? "ZIP code data is extensive and data loading may take a few moments."
            : "Neighborhood data is extensive and data loading may take a few moments."
          }
        </div>
      )}

      {/* Error indicator */}
      {currentFeature.error && (
        <div style={{
          margin: "6px 0 10px",
          padding: "8px 12px",
          background: "#ffebee",
          border: "1px solid #f44336",
          borderRadius: "6px",
          fontSize: "13px",
          color: "#c62828"
        }}>
          Error: {currentFeature.error}
        </div>
      )}

      {/* Path panel */}
      {(() => {
        const shouldShowPath = selectedMetro || selectedName;
        if (!shouldShowPath) return null;
        const pathContent = (() => {
          const targetName = selectedMetro || selectedName;
          
          if (!targetName) return "Loading location data...";
          
          // Try to get path from crumbsByName first (has full hierarchy)
          const crumbs = crumbsByName?.[targetName];
          if (crumbs) {
            const crumbsWithCurrent = { ...crumbs };
            if (areaType === "Metro" && !crumbsWithCurrent.Metro) {
              crumbsWithCurrent.Metro = targetName;
            } else if (areaType === "City" && !crumbsWithCurrent.City) {
              crumbsWithCurrent.City = targetName;
            } else if (areaType === "County" && !crumbsWithCurrent.County) {
              crumbsWithCurrent.County = targetName;
            } else if (areaType === "Zipcode" && !crumbsWithCurrent.Zipcode) {
              crumbsWithCurrent.Zipcode = targetName;
            } else if (areaType === "Neighborhood" && !crumbsWithCurrent.Neighborhood) {
              crumbsWithCurrent.Neighborhood = targetName;
            }
            
            const pathLabel = smartCrumbChainLabel(crumbsWithCurrent, areaType);
            return pathLabel || "No path data available";
          }
          
          // Fallback to currentMetro
          if (currentMetro) {
            const currentWithSelected = { ...currentMetro };
            if (areaType === "Metro" && !currentWithSelected.Metro) {
              currentWithSelected.Metro = targetName;
            } else if (areaType === "City" && !currentWithSelected.City) {
              currentWithSelected.City = targetName;
            } else if (areaType === "County" && !currentWithSelected.County) {
              currentWithSelected.County = targetName;
            } else if (areaType === "Zipcode" && !currentWithSelected.Zipcode) {
              currentWithSelected.Zipcode = targetName;
            } else if (areaType === "Neighborhood" && !currentWithSelected.Neighborhood) {
              currentWithSelected.Neighborhood = targetName;
            }
            
            const pathLabel = smartCrumbChainLabel(currentWithSelected, areaType);
            return pathLabel || "No path data available";
          }
          
          return "No path data available";
        })();

        return (
          <>
            <div
              style={{
                margin: "4px 0 0",
                padding: "6px 12px",
                background: "#fff",
                border: "1px solid #e6ebf3",
                borderRadius: 8,
                fontSize: 13,
                minHeight: "32px",
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <strong>Path:</strong> {pathContent}
            </div>
            {view !== "performance" &&
              view !== "performance-snapshot" &&
              view !== "newMarketsTP" &&
              view !== "newMarketsTP-snapshot" &&
              renderRobotHint()}
          </>
        );
      })()}

      {/* CONTENT - ISOLATED FEATURE RENDERING */}
      {view === "newMarketsTP"
        ? DataTable.renderBestPotentialTable({ 
            bestPotential: feature4Data.bestPotential, 
            latestByMetro: feature4Data.latestByMetro, 
            setSelectedMetro, 
            setSelectedName,
            setView, 
            allDates: feature4Data.allDates, 
            latestDate: feature4Data.latestDate, 
            areaType,
            snapshotView: 'newMarketsTP-snapshot',
            afterHeading: renderRobotHint({ marginTop: 8, marginBottom: -6 })
          })
        : view === "newMarketsTP-snapshot"
        ? (
          <>
            <MetroDataTable
              currentMetro={currentMetro}
              selectedMetro={selectedMetro}
              showChart={showChart}
              allDates={feature4Data.allDates}
              latestDate={feature4Data.latestDate}
              forecastByName={{}}
              areaType={areaType}
              linksByName={{}}
              setAreaType={setAreaType}
              setSelectedMetro={setSelectedMetro}
              setSelectedName={setSelectedName}
              setView={setView}
              showExplore={false}
            />
              {/* Loading indicator for chart data - only for Zipcode/Neighborhood */}
              {isLoading && (areaType === 'Zipcode' || areaType === 'Neighborhood') && (
                <div style={{
                  margin: "20px 0 10px",
                  padding: "12px 16px",
                  background: "#e3f2fd",
                  border: "1px solid #90caf9",
                  borderRadius: "8px",
                  fontSize: "14px",
                  color: "#1565c0",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px"
                }}>
                  <div style={{
                    width: "18px",
                    height: "18px",
                    border: "3px solid #e3f2fd",
                    borderTop: "3px solid #1565c0",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    flexShrink: 0
                  }} />
                  Loading chart data...
                </div>
              )}
              {feature4Data.chartData?.length > 0 && (
                <>
                  {(() => {
                    // CRITICAL FIX: Use RegionID from chart data (Markets_TP source) 
                    // instead of currentMetro (snapshot data source) for TP lookup
                    // Chart data and TP data both come from Markets_TP files with matching RegionIDs
                    const chartDataRegionId = feature4Data.chartData?.[0]?.regionId;
                    const currentMetroRegionId = currentMetro?.RegionID || currentMetro?.regionId || currentMetro?.region_id;
                    
                    // Prefer chart data RegionID since it matches TP data source
                    const selectedRegionId = chartDataRegionId || currentMetroRegionId;
                    
                    const regionIdMismatch = chartDataRegionId && currentMetroRegionId && chartDataRegionId !== currentMetroRegionId;
                    
                    console.error(`[DASHBOARD_ISOLATED] 🔍 FEATURE 4 TP MATCHING DEBUG:`, {
                      selectedMetro,
                      selectedName,
                      currentMetro: currentMetro ? {
                        RegionID: currentMetroRegionId,
                        Neighborhood: currentMetro.Neighborhood,
                        Metro: currentMetro.Metro,
                        City: currentMetro.City,
                        County: currentMetro.County,
                        State: currentMetro.State
                      } : null,
                      chartDataRegionId,
                      currentMetroRegionId,
                      selectedRegionId_FINAL: selectedRegionId,
                      regionIdMismatch,
                      tpDataForChartDataRegionId: chartDataRegionId ? feature4Data.tpByRegionId?.[chartDataRegionId] : null,
                      tpDataForCurrentMetroRegionId: currentMetroRegionId ? feature4Data.tpByRegionId?.[currentMetroRegionId] : null,
                      tpByRegionIdSize: Object.keys(feature4Data.tpByRegionId || {}).length,
                      chartDataLength: feature4Data.chartData?.length
                    });
                    
                    return null;
                  })()}
                  <OptimizedChart
                    chartData={feature4Data.chartData}
                    selectedMetro={selectedMetro}
                    tpByMetro={feature4Data.tpByMetro || {}}
                    tpByRegionId={feature4Data.tpByRegionId || {}}
                    selectedRegionId={
                      // CRITICAL FIX: Use chart data RegionID (matches TP data source) first
                      feature4Data.chartData?.[0]?.regionId || 
                      currentMetro?.RegionID || 
                      currentMetro?.regionId || 
                      currentMetro?.region_id
                    }
                    areaType={areaType}
                    title={currentChartMetric}
                  />
                </>
              )}
              {console.log(`[DASHBOARD_ISOLATED] Feature 4 chart data:`, {
                chartDataLength: feature4Data.chartData?.length,
                selectedMetro,
                areaType,
                currentChartMetric
              })}
          </>
        )
        : view === "performance"
        ? DataTable.renderRankTable({ 
            type: "performance", 
            ranks: feature2Data.ranks, 
            scores: feature2Data.scores, 
            latestByMetro: feature2Data.latestByMetro, 
            latestDate: feature2Data.latestDate, 
            showChart, 
            allDates: feature2Data.allDates, 
            areaType, 
            linksByName, 
            setAreaType, 
            setSelectedMetro, 
            setSelectedName, 
            setView, 
            forecastByName: {},
            afterHeading: renderRobotHint({ marginTop: 8, marginBottom: -6 })
          })
        : view === "performance-snapshot"
        ? (
          <>
            {console.log("[DASHBOARD_ISOLATED] Rendering Feature 2 snapshot:", {
              currentMetro,
              selectedMetro,
              feature2Data: {
                allDates: feature2Data.allDates?.length || 0,
                latestByMetro: Object.keys(feature2Data.latestByMetro || {}),
                latestDate: feature2Data.latestDate,
                chartData: feature2Data.chartData?.length || 0
              },
              areaType
            })}
            <MetroDataTable
              currentMetro={currentMetro}
              selectedMetro={selectedMetro}
              showChart={showChart}
              allDates={feature2Data.allDates}
              latestDate={feature2Data.latestDate}
              forecastByName={{}}
              areaType={areaType}
              linksByName={linksByName}
              linksByRegionId={linksByRegionId}
              setAreaType={setAreaType}
              setSelectedMetro={setSelectedMetro}
              setSelectedName={setSelectedName}
              setView={setView}
            />
            {/* Loading indicator for chart data - only for Zipcode/Neighborhood */}
            {isLoading && (areaType === 'Zipcode' || areaType === 'Neighborhood') && (
              <div style={{
                margin: "20px 0 10px",
                padding: "12px 16px",
                background: "#e3f2fd",
                border: "1px solid #90caf9",
                borderRadius: "8px",
                fontSize: "14px",
                color: "#1565c0",
                display: "flex",
                alignItems: "center",
                gap: "10px"
              }}>
                <div style={{
                  width: "18px",
                  height: "18px",
                  border: "3px solid #e3f2fd",
                  borderTop: "3px solid #1565c0",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  flexShrink: 0
                }} />
                Loading chart data...
              </div>
            )}
            {feature2Data.chartData?.length > 0 && (
              <OptimizedChart
                chartData={feature2Data.chartData}
                selectedMetro={selectedMetro}
                tpByMetro={{}}
                areaType={areaType}
                title={currentChartMetric}
              />
            )}
            {renderRobotHint({ marginTop: 12, marginBottom: 12 })}
          </>
        )
        : view === "metro" || (view === "" && (selectedMetro || selectedName))
        ? (
          <>
            {/* For all levels, show best performing table when no specific selection is made */}
            {!(selectedMetro || selectedName) ? (
              DataTable.renderRankTable({
                type: "performance",
                ranks: feature1Data.ranks,
                scores: feature1Data.scores,
                latestByMetro: feature1Data.latestByMetro,
                latestDate: feature1Data.latestDate,
                showChart,
                allDates: feature1Data.allDates,
                areaType,
                linksByName: {},
                setAreaType,
                setSelectedMetro,
                setSelectedName,
                setView,
                forecastByName: {}
              })
            ) : (
              <>
                <MetroDataTable
                  currentMetro={currentMetro}
                  selectedMetro={selectedMetro}
                  showChart={showChart}
                  allDates={feature1Data.allDates}
                  latestDate={feature1Data.latestDate}
                  forecastByName={{}}
                  areaType={areaType}
                  linksByName={{}}
                  showExplore={false}
                  setAreaType={setAreaType}
                  setSelectedMetro={setSelectedMetro}
                  setSelectedName={setSelectedName}
                  setView={setView}
                />
                {/* Loading indicator for chart data - only for Zipcode/Neighborhood */}
                {isLoading && (areaType === 'Zipcode' || areaType === 'Neighborhood') && (
                  <div style={{
                    margin: "20px 0 10px",
                    padding: "12px 16px",
                    background: "#e3f2fd",
                    border: "1px solid #90caf9",
                    borderRadius: "8px",
                    fontSize: "14px",
                    color: "#1565c0",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px"
                  }}>
                    <div style={{
                      width: "18px",
                      height: "18px",
                      border: "3px solid #e3f2fd",
                      borderTop: "3px solid #1565c0",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                      flexShrink: 0
                    }} />
                    Loading chart data...
                  </div>
                )}
                {feature1Data.chartData?.length > 0 && (
                  <OptimizedChart
                    chartData={feature1Data.chartData}
                    selectedMetro={selectedMetro}
                    tpByMetro={{}}
                    areaType={areaType}
                    title={currentChartMetric}
                  />
                )}
              </>
            )}
          </>
        )
        : null}

      {/* FEATURE 6 DISABLED - Ask Me a Question (AI) UI section removed */}
      {/* To re-enable: Uncomment the button above and this section below */}
      {/* {showAskBox && (
        <div style={{ marginTop: "0px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0px", paddingBottom: "8px", position: "relative", zIndex: 1 }}>
            <h3 style={{ margin: "0px 0px 0px 0px", padding: "0px" }}>Ask Me Questions - Get More Insights (AI)</h3>
          </div>
          <div style={{ 
            padding: "16px",
            marginTop: "2px",
            background: "#ffffff",
            border: "1px solid #e6ebf3",
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(20,40,80,0.04)",
            maxWidth: "800px",
            maxHeight: "600px",
            display: "flex",
            flexDirection: "column"
          }}>
            <div style={{ marginBottom: "16px" }}>
              <textarea
                placeholder="Ask me about features, specific area performance, and much more..."
                value={aiChat.question}
                onChange={(e) => aiChat.setQuestion(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey && !aiChat.loading && aiChat.question.trim()) {
                    e.preventDefault();
                    aiChat.handleAsk();
                  }
                }}
                rows={3}
                style={{ 
                  width: "100%", 
                  boxSizing: "border-box",
                  padding: "10px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  resize: "vertical",
                  fontFamily: "inherit"
                }}
                disabled={aiChat.loading}
              />
              <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                {aiChat.messages.length > 0 && (
                  <button 
                    className="btn" 
                    onClick={aiChat.reset}
                    style={{ 
                      padding: "6px 12px", 
                      fontSize: "12px",
                      background: "#f0f0f0",
                      border: "1px solid #ddd",
                      minWidth: "100px"
                    }}
                  >
                    Clear Chat
                  </button>
                )}
                <button 
                  className="btn" 
                  onClick={aiChat.handleAsk} 
                  disabled={aiChat.loading || !aiChat.question.trim()}
                  style={{ minWidth: "100px" }}
                >
                  {aiChat.loading ? "Sending..." : "Send"}
                </button>
              </div>
            </div>
            
            <div 
              ref={chatMessagesRef}
              style={{ 
                flex: 1,
                overflowY: "auto",
                minHeight: "240px",
                maxHeight: "340px",
                padding: "8px",
                background: "#f9fafb",
                borderRadius: "8px",
                borderTop: "1px solid #e6ebf3",
                paddingTop: "12px"
              }}
            >
              {aiChat.messages.length > 0 && (
                aiChat.messages.map((msg, idx) => (
                  <div
                    key={idx}
                    style={{
                      marginBottom: "12px",
                      display: "flex",
                      flexDirection: msg.role === "user" ? "row-reverse" : "row",
                      alignItems: "flex-start"
                    }}
                  >
                    <div style={{
                      maxWidth: "75%",
                      padding: "10px 14px",
                      borderRadius: "12px",
                      background: msg.role === "user" 
                        ? "#007bff" 
                        : (msg.content.includes("Error") || msg.content.includes("error") ? "#fee" : "#efe"),
                      color: msg.role === "user" ? "#fff" : "#333",
                      border: msg.role === "user" 
                        ? "none" 
                        : `1px solid ${msg.content.includes("Error") || msg.content.includes("error") ? "#fcc" : "#cfc"}`,
                      wordWrap: "break-word",
                      whiteSpace: "pre-wrap"
                    }}>
                      {msg.content}
                    </div>
                  </div>
                ))
              )}
              {aiChat.loading && (
                <div style={{ 
                  color: "#666", 
                  fontStyle: "italic",
                  padding: "8px",
                  display: "flex",
                  alignItems: "center"
                }}>
                  <span style={{ marginRight: "8px" }}>Thinking...</span>
                  <span style={{ 
                    display: "inline-block",
                    width: "12px",
                    height: "12px",
                    border: "2px solid #ccc",
                    borderTopColor: "#007bff",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite"
                  }}></span>
                </div>
              )}
            </div>
          </div>
        </div>
      )} */}

      {/* Disclaimer */}
      {(view === "bestPotential" || view === "performance" || view === "bestPotential-snapshot" || view === "performance-snapshot" || view === "newMarketsTP" || view === "newMarketsTP-snapshot" || view === "metro") && (
        <div style={{
          marginTop: 'auto',
          padding: '24px 16px',
          textAlign: 'center',
          fontSize: 13,
          color: '#666',
          borderTop: '1px solid #e5e7eb'
        }}>
          AlgoreitAI can make mistakes. User must verify the info.
        </div>
      )}

      {/* Executive Report Modal */}
      {showReport && (
        <ExecutiveReport
          onClose={() => setShowReport(false)}
          areaType={areaType}
          selectedName={selectedMetro || selectedName}
          latestByMetro={currentFeature.data?.latestByMetro || {}}
          chartData={currentFeature.data?.chartData || []}
          forecastByName={{}}
          allDates={currentFeature.data?.allDates || []}
          crumbsByName={crumbsByName || {}}
        />
      )}

      {/* Guide Me Modal */}
      {showGuideMe && (
        <GuideMe
          onClose={() => setShowGuideMe(false)}
          initialSection={guideSection}
        />
      )}
    </div>
  );
}
